import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Professional } from '@auth/interfaces/professional.interface';
import {
  MAIL_RADIOTHERAPY_CSC,
  MAIL_RADIOTHERAPY_MARCOLETA,
  NURSES_MC_MAILS,
  NURSES_SC_MAILS,
} from '@clinical/oncology/constants/oncology-constant';
import { FormsOncology } from '@clinical/oncology/enums/encology.enum';
import { Patient } from '@components/patient/interfaces/patient.interface';
import { environment } from '@environments/environment';
import { Store } from '@ngrx/store';
import { API_URIS } from '@shared/constants/api-uris.const';
import { resolvePromises } from '@shared/helpers/allSettled.helper';
import { Email, ShortPathFile, Sms } from '@shared/interfaces/notification.interface';
import { Metadata, PCV2Request } from '@shared/interfaces/request.interface';
import { Attachment } from '@shared/interfaces/storage.interface';
import { DateService } from '@shared/services/date.service';
import { mailPartialTemplates, mailTemplates } from '@shared/templates/emails/mail-templates';
import { getMailTeamAccessText } from '@shared/templates/team-access.const';
import { AppState } from '@store/app.reducers';
import { PaperworkState } from '@store/reducers';
import { KEY_PROFESSIONAL_STORE } from '@store/store-keys';
import { firstValueFrom, lastValueFrom, Observable, of, Subject } from 'rxjs';
import { catchError, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { AddressService } from 'src/app/pharmacyportal/services/address.service';
import { QuotationService } from 'src/app/pharmacyportal/services/quotation.service';
import { Paperwork, WorkflowNotify } from '@shared/interfaces/paperwork.interface';
import { AmbulatoryAgendaService } from '@clinical/indications/services/ambulatory-agenda.service';
import { SpecialtyByService } from '@clinical/indications/interfaces/ambulatory-agenda.interface';
import { AddressPatientPharma } from '../../pharmacyportal/interfaces/address.interface';
import { EMAIL_DERIVATE_BY_PROFESIONAL } from '@clinical/surgeries/constants/derivate-nurse.constant';
import { NotificationEmailsService } from '@shared/services/notification-emails.service';
import { TENS_PROFILES_NO_PATIENT_MAIL } from '@auth/constants/profiles.const';
import { MAIL_DERIVATIONS, SPECIALTIES } from '@clinical/derivations/constants/derivation.constant';
import { paperwork } from '../../store/actions/paperwork.actions';
import { EMAILS } from '@shared/constants/notification-emails.const';

const BASE_API_PCV2 = environment.baseApiPCv2;
const BASE_AGENDA_UC = environment.baseApiAgendaUc;
const URI_NOTIFICATIONS_V1 = API_URIS.notifications.v1;
const APP_DOMAIN: string = environment.appDomain;
const DOMAIN: string = environment.domain;
const EXAM_PATH_NOTIFICATION = environment.examAreasIndications;
const PREFIX_ONCOLOGY = 'ONC';
const PREFIX_INDICATION = 'IND';
const PREFIX_SURGERY = 'SUR';
const PREFIX_RECIPE = 'RME';
const CENTER_MC = 'Hospital Marcoleta';
const CENTER_CSC = 'Clínica San Carlos';
const STAGE: string = environment.stage || 'dev';

/**
 * Servicio para administrar los diferentes tipos de notificaciones.
 */
@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  destroy = new Subject();
  storeProfessional: any;
  textMentalHealth: string;
  textKine: string;
  genericCEM: string = 'genericCEM';

  /**
   * @ignore
   */
  constructor(
    private httpClient: HttpClient,
    private quoService: QuotationService,
    private notificationEmailsService: NotificationEmailsService,
    private ambulatoryAgendaService: AmbulatoryAgendaService,
    public addressService: AddressService,
    private store: Store<AppState>,
  ) {
    this.readStoreProfessional();
  }

  readStoreProfessional(): void {
    this.store
      .select(KEY_PROFESSIONAL_STORE)
      .pipe(
        takeUntil(this.destroy),
        filter(({ isLoaded, professional }) => isLoaded && professional !== undefined),
        map((professional) => (this.storeProfessional = professional)),
      )
      .subscribe();
  }

  /**
   * es sub-profesional (TENS) que no debe enviar correos al paciente
   */
  isNoMailSubprofessionalTENS(): boolean {
    if (!this.storeProfessional?.subprofessional) return false;
    return this.storeProfessional.subprofessional.profiles.some((profile: string) =>
      TENS_PROFILES_NO_PATIENT_MAIL.includes(profile),
    );
  }

  /**
   * Servicio para Enviar Mensajes de Textos.
   *
   * @param sms - Cuerpo de la petición para enviar mensaje de texto.
   */
  sendSMS(sms: Sms): Promise<any> {
    const url = `${BASE_API_PCV2}/${URI_NOTIFICATIONS_V1}/sms`;
    return lastValueFrom(this.httpClient.post<any>(url, sms));
  }

  /**
   * Servicio para Enviar Correo Electrónico.
   *
   * @param email Cuerpo de la petición para enviar mensaje de correo electrónico.
   */
  sendEmail(email: Email): Observable<Metadata> {
    const url = `${BASE_API_PCV2}/${URI_NOTIFICATIONS_V1}/correo`;

    return this.httpClient.post<PCV2Request>(url, email).pipe(map(({ metadata }) => metadata));
  }

  /**
   * Obtener el nombre completo de un profesional o paciente.
   *
   * @param entityType Puede recibir las opciones de patient o professional.
   * @param entity Objeto del profesional o paciente que cumpla con las interfaces de Patient y/o Professional.
   */
  getEntityFullnameToUpperCase(entityType: 'patient' | 'professional', entity: Patient | Professional): string {
    if (!entity) return '';
    const names = entity.names ? entity.names.toUpperCase() : '';
    const surnames = entity.surnames ? entity.surnames.toUpperCase() : '';

    return (names + ' ' + surnames).trim();
  }

  /**
   * Obtener el centro id desde la ruta actual.
   *
   * @returns Retorna el id del centro.
   */
  private getCenterFromRouteParam = (): string => {
    const path = window.location.href.split('/');
    if (Array.isArray(path)) return path.pop();
  };

  /**
   * Obtener el cuerpo del correo electrónico en HTML.
   *
   * @param template Nombre de la plantilla de correo que se debe usar para enviar el correo electrónico.
   * @param paperworkState Trámite extraído del estado global de la aplicación.
   * @param additionalContent información opcional necesaria para algunas plantillas de correo
   */
  async getEmailBodyHTML(template: string, paperworkState: PaperworkState, additionalContent: any): Promise<string> {
    const { paperwork, definition } = paperworkState;
    const suffix = definition.submodule.suffix;

    if (template === 'recipe') return await this.getRecipeEmailBodyHTML(paperworkState, additionalContent);
    if (template === 'exams') return await this.getExamsEmailBodyHTML(paperworkState, additionalContent);
    if (template === 'educational') return await this.getEducationalEmailBodyHTML(paperworkState);
    if (template === 'derivations') return await this.getDerivationsEmailBodyHTML(paperworkState);
    if (template === 'surgery') return await this.getSurgeriesEmailBodyHTML(paperworkState, template);
    if (template === 'indications' && ['EOF', 'DRM', 'OTR'].includes(suffix))
      return await this.getIndicationsBodyHTML(paperworkState, additionalContent);

    const docTitle = definition.submodule.docTitle.toUpperCase();
    const shortDate = await DateService.datetimeCL('short');
    const centerId = this.getCenterFromRouteParam() || '';

    const site = paperwork?.content?.[0]?.site ?? '';
    let textBodyRadiotherapy = '';

    if (site === 'Hospital Marcoleta') {
      textBodyRadiotherapy = this.getRadiotherapy();
    }

    return mailTemplates[template]
      .replace('$shortDate$', shortDate)
      .replace('$paperworkTitle$', docTitle)
      .replace('$patientFullname$', this.getEntityFullnameToUpperCase('patient', paperwork.patient))
      .replace('$patientDocumentNumber$', paperwork.patient?.documentNumber || '')
      .replace('$professionalFullname$', this.getEntityFullnameToUpperCase('professional', paperwork.professional))
      .replace('$professionalSpecialty$', (paperwork.professional?.specialty?.name || '').toUpperCase())
      .replace('$bodyGeneric$', this.getTemplateByCenterId(centerId))
      .replace('$teamAccess$', this.getNotifyAccessTeamContent(paperworkState))
      .replace('$linksButtons$', '')
      .replace('$bodyRadiotherapy$', textBodyRadiotherapy)
      .replace('$informationMessage$', this.getInformationMessage());
  }

  getInformationMessage(): string {
    return `<tr style="box-sizing: border-box; display: none"
                            ><th style="box-sizing: border-box"></th
                          ></tr>
                          <tr  style="
                          box-sizing: border-box;
                          width: 100%;
                          padding: 24px 24px;
                          background: #F5F0FF;
                        ">
                            <td
                              style="
                                box-sizing: border-box;
                                padding-bottom: 0px;
                                padding-top: 30px;
                                padding-left: 20px;
                                vertical-align: top;
                                width: 30%;
                                text-align: left;
                              ">
                              <img
                                src="https://cdn-icons-png.flaticon.com/512/6915/6915657.png"
                                alt="image"
                                style="box-sizing: border-box"
                                class="CToWUd"
                                data-bit="iit"
                                width="100" />
                            </td>
                            <td
                              style="
                                box-sizing: border-box;
                                padding-bottom: 0px;
                                padding-top: 0px;
                                padding-left: 0px;
                                vertical-align: center;
                                width: 70%;
                                text-align: left;
                              ">

                              <h4
                                style="
                                  box-sizing: border-box;
                                  font-weight: 700;
                                  font-size: 16px;
                                  line-height: 20px;
                                  color: #373737;
                                  margin: 0;
                                  margin-bottom: 10px;
                                  padding-top:20px;
                                "
                                >Información</h4
                              >

                              <p style="box-sizing: border-box; margin-bottom: 8px; margin-top: 0; display: flex;text-align: justify;padding-right:30px;"
                                ><span style="box-sizing: border-box; font-weight: 400; font-size: 14px; color: #373737"
                                  >
                                  Aquí podrás encontrar todas las indicaciones que tu médico tratante ha indicado
                que debes realizar para continuar con tu tratamiento o diagnóstico.
                                  </span
                                ></p
                              >
                            </td>
                          </tr>`;
  }

  async getIndicationsBodyHTML(paperworkState: PaperworkState, linksButtons: string): Promise<string> {
    const { paperwork, definition } = paperworkState;
    const docTitle = definition.submodule.docTitle.toUpperCase();
    const shortDate = await DateService.datetimeCL('short');

    return mailTemplates.indications
      .replace('$shortDate$', shortDate)
      .replace('$paperworkTitle$', docTitle)
      .replace('$patientFullname$', this.getEntityFullnameToUpperCase('patient', paperwork.patient))
      .replace('$professionalFullname$', this.getEntityFullnameToUpperCase('professional', paperwork.professional))
      .replace('$professionalSpecialty$', (paperwork.professional?.specialty?.name || '').toUpperCase())
      .replace('$informationMessage$', '')
      .replace('$linksButtons$', linksButtons || '');
  }

  private getTemplateByCenterId(centerId: string) {
    if (centerId === 'cem') return mailTemplates[this.genericCEM];
    return '';
  }

  async getDerivationsEmailBodyHTML(paperworkState: PaperworkState): Promise<string> {
    const DER = 'Unidad Salud Mental Integral Psiquiatria o Psicologia';
    const KIN = 'Kinesiología';

    if (
      paperworkState.paperwork.content[0].interconsultation[0]?.name === DER ||
      paperworkState.paperwork.content[0].service?.derivation?.specialty === DER
    )
      this.textMentalHealth = `
    <div style="text-align: left;color: #5a5a5a;font-size: 9px;line-height: 20px;">
    <p>Unidad de Salud Mental Integral - Psiquiatria o Psicologia</p>
    <p>Centro Médico Providencia</p>
    <p>Teléfono de Contacto: 224820056</p>
    <p>Correo de Contacto: <a href="mailto:contacto.providencia@ucchristus.cl"></a>contacto.providencia@ucchristus.cl</p>
    <p>Más información en <a href="https://www.ucchristus.cl/especialidades-y-servicios/unidades/unidad-salud-mental-integral-y-recepcion">Unidad Salud Mental Integral - Psiquiatría o Psicología</a></p>
    </div>
    <p>AGENDA TU HORA CON PROFESIONALES DE SALUD MENTAL <a href="https://agenda.ucchristus.cl/reserva-horas/seleccion/dia?area=aafc27df-882e-413b-bdcd-a96e00ddbc15&specialty=c15ed2e8-e79e-43f8-af83-a96e0147e05b&service=62bcb92f-d0b1-40ee-9f37-afc7011305aa&center=todos&region=6bad9b25-d5df-4565-b5fe-a6f701444053&coveragePlan=0b7a577d-6364-4b28-b2ba-a96e00e243ac ">AQUÍ</a></p>
    <br/>

    `;
    else this.textMentalHealth = '';

    if (
      paperworkState.paperwork.content[0].interconsultation[0]?.name === KIN ||
      paperworkState.paperwork.content[0].service?.derivation?.specialty === KIN
    )
      this.textKine = `
    <p>AGENDA TU HORA CON PROFESIONALES DE KINESIOLOGÍA <a href="https://agenda.ucchristus.cl/reserva-horas/busqueda?area=7a9d5f62-8899-4a5c-a28a-a9730138ecdd&specialty=5a012a01-2b3e-4b5f-96aa-a97400d58b67 ">AQUÍ</a></p>
     <p>
          <img src="https://s3.us-west-2.amazonaws.com/recursosweb.pub.prd.usw2/correos/agendar-hora.png" width="700" />
        </p>
    `;
    else this.textKine = '';

    const { paperwork, definition } = paperworkState;
    const isIntegraHealth = paperwork.content[0]?.integralHealth;
    const docTitle = definition.submodule.docTitle.toUpperCase();
    const shortDate = await DateService.datetimeCL('short');
    return mailTemplates.derivations
      .replace('$shortDate$', shortDate)
      .replace('$paperworkTitle$', docTitle)
      .replace('$patientFullname$', this.getEntityFullnameToUpperCase('patient', paperwork.patient))
      .replace('$patientDocumentNumber$', paperwork.patient?.documentNumber || '')
      .replace('$professionalFullname$', this.getEntityFullnameToUpperCase('professional', paperwork.professional))
      .replace('$professionalSpecialty$', (paperwork.professional?.specialty?.name || '').toUpperCase())
      .replace('$teamAccess$', this.getNotifyAccessTeamContent(paperworkState))
      .replace('$text$', this.textMentalHealth)
      .replace('$textKine$', this.textKine)
      .replace('$footerText$', isIntegraHealth === 'OK' ? this.getMessageDerivation() : '');
  }

  getMessageDerivation() {
    return (
      'Esta derivación es válida por un año desde la fecha de emisión \n' +
      'y cubre únicamente los controles indicados con la especialidad \n' +
      'señalada (siempre que se encuentre cubierta por su póliza \n' +
      'o convenio y haya seguido el flujo de atención). \n' +
      'Si requiere de más controles, puede agendar una atención con \n' +
      'su médico de cabecera o escribir por WhatsApp al equipo clínico.\n' +
      ''
    );
  }

  async getSurgeriesEmailBodyHTML(paperworkState: PaperworkState, template: string): Promise<string> {
    const { paperwork, definition } = paperworkState;

    const docTitle = definition.submodule.docTitle.toUpperCase();
    const shortDate = await DateService.datetimeCL('short');

    return mailTemplates[template]
      .replace('$shortDate$', shortDate)
      .replace('$paperworkTitle$', docTitle)
      .replace('$patientFullname$', this.getEntityFullnameToUpperCase('patient', paperwork.patient))
      .replace('$professionalFullname$', this.getEntityFullnameToUpperCase('professional', paperwork.professional));
  }

  async getRecipeEmailBodyHTML(paperworkState: PaperworkState, quotationByPharmacy: string): Promise<string> {
    const { paperwork, definition } = paperworkState;
    const recipeUrl = `https://${APP_DOMAIN}${DOMAIN}/farmacia/checkout/${paperwork.shortId}`;
    const docTitle = definition.submodule.docTitle.toUpperCase();
    const shortDate = await DateService.datetimeCL('short');
    return mailTemplates.recipe
      .replace('$shortDate$', shortDate)
      .replace('$paperworkTitle$', docTitle)
      .replace('$patientFullname$', this.getEntityFullnameToUpperCase('patient', paperwork.patient))
      .replace('$professionalFullname$', this.getEntityFullnameToUpperCase('professional', paperwork.professional))
      .replace('$url$', recipeUrl)
      .replace('$quotation$', quotationByPharmacy);
  }

  async getExamsEmailBodyHTML(paperworkState: PaperworkState, linksButtons: string): Promise<string> {
    const { paperwork, definition } = paperworkState;
    const docTitle = definition.submodule.docTitle.toUpperCase();
    const shortDate = await DateService.datetimeCL('short');
    return mailTemplates.exams
      .replace('$shortDate$', shortDate)
      .replace('$paperworkTitle$', docTitle)
      .replace('$patientFullname$', this.getEntityFullnameToUpperCase('patient', paperwork.patient))
      .replace('$professionalFullname$', this.getEntityFullnameToUpperCase('professional', paperwork.professional))
      .replace('$patientFullnameSubject$', this.getEntityFullnameToUpperCase('patient', paperwork.patient).trim())
      .replace('$professionalFullnameSubject$', this.getEntityFullnameToUpperCase('professional', paperwork.professional).trim())
      .replace('$professionalSpecialty$', (paperwork.professional?.specialty?.name || '').toUpperCase())
      .replace('$linksButtons$', linksButtons || '')
      .replace('$teamAccess$', this.getNotifyAccessTeamContent(paperworkState));
  }

  async getEducationalEmailBodyHTML(paperworkState: PaperworkState): Promise<string> {
    const { paperwork, definition } = paperworkState;
    const docTitle = definition.submodule.docTitle.toUpperCase();
    const shortDate = await DateService.datetimeCL('short');
    const materials = [];
    paperwork.content.map((content) => {
      content.documents.map((doc) => materials.push(`<li><a href='${doc.url}' target='_blank'>${doc.name}</a></li>`));
    });
    return mailTemplates.educational
      .replace('$shortDate$', shortDate)
      .replace('$paperworkTitle$', docTitle)
      .replace('$patientFullname$', this.getEntityFullnameToUpperCase('patient', paperwork.patient))
      .replace('$professionalFullname$', this.getEntityFullnameToUpperCase('professional', paperwork.professional))
      .replace('$materials$', materials.join(' '));
  }

  /**
   * Obtiene lista de precios de medicamentos por farmacia para un paciente
   */
  getQuotePharma(recipe: Paperwork) {
    const { patient } = recipe;
    return this.addressService.getPatientAddress(patient.id).pipe(
      map(([firstAddress]) => firstAddress),
      tap((firstAddress) => this.validCommuneId(firstAddress.address?.comune?.id)),
      map((firstAddress) => this.getParamsToSearchQuote(recipe, firstAddress)),
      switchMap((params) => this.quoService.getPharmByRmeId(params)),
      map((quote) => ({ quote, recipe })),
      catchError((error) => of({ quote: null, recipe: null })),
    );
  }

  getParamsToSearchQuote(recipe: Paperwork, address: AddressPatientPharma) {
    /* eslint-disable @typescript-eslint/naming-convention */
    // TODO: No se deben eliminar las 3 propiedades siguientes, hasta que se confirme si el servicio de yapp va a necesitar estos datos
    const { patient, professional, content } = recipe;
    const formula = content.map((prescription) => ({ id: prescription.medicine?.id }));
    return {
      medical: professional,
      patient,
      prescription: content,
      formula,
      commune_id: address?.address?.comune?.id,
      formatted_address: address?.formatted_address,
      address: address?.address,
    };
  }

  validCommuneId(communeId: any): any {
    if (!communeId) throw new Error('communeId is null');
    return communeId;
  }

  /**
   * Obtiene sección de cotización de farmacias para incorporar en el correo de recetas
   *
   * @param recipe
   */
  async getRecipeQuotePharmaEmailBodyHTML(recipe: Paperwork) {
    const $quotePharma = this.getQuotePharma(recipe);
    const { quote } = await firstValueFrom($quotePharma);
    if (!quote || !quote.data?.length) return '';

    const recipeBaseUrl = `https://${APP_DOMAIN}${DOMAIN}/farmacia/checkout/${recipe.shortId}`;
    const patientId = recipe.patient?.id;
    const pharmaciesQuote = quote.data
      .filter((pharm) => pharm.delivery.cost !== null)
      .map((pharm) => {
        const minmax = pharm.quotation_detail.sort((a, b) => a.price - b.price);
        const price =
          minmax.length > 1 ? `$ ${minmax[0]?.price} - $ ${minmax[minmax.length - 1]?.price}` : `$ ${minmax[0]?.price}`;
        return mailPartialTemplates.pharmacyQuote
          .replace('$name$', pharm.pharmacy_chain_name)
          .replace('$logo$', pharm.pharmacy_chain_logo)
          .replace('$pharmacyUrl$', `${recipeBaseUrl}/paciente/${patientId}/farmacia/${pharm.pharmacy_chain_id}`)
          .replace('$deliveryDate$', pharm.delivery?.next_date || '')
          .replace('$deliveryCost$', pharm.delivery.cost)
          .replace('$price$', price);
      });
    return mailPartialTemplates.pharmaciesSelection.replace('$items$', pharmaciesQuote.join(''));
  }

  /**
   * Obtiene lista de especialidades para indicaciones (exámenes) de un trámite
   *
   * @param exams lista de exámenes
   */
  async getIndicationsSpecialtyByService(exams: any[]): Promise<SpecialtyByService[]> {
    const idServiceRequests = exams
      .filter((exam) => exam.booking && exam.booking === 'link')
      .map((exam) => {
        const ambulatoryAgenda$ = this.ambulatoryAgendaService.getAmbulatoryAgenda(exam);
        return firstValueFrom(ambulatoryAgenda$);
      });
    const [indicationsServicesIds] = await resolvePromises(idServiceRequests);
    return indicationsServicesIds
      .filter((item) => item && item.statusCod === 'OK')
      .map((item) => item.especialidadesPorServicio)
      .flat();
  }

  /**
   * Obtiene links de indicaciones para incluir en correo cuando corresponda
   *
   * @param indication
   */
  async getIndicationLink(indication: Paperwork) {
    const { content, patient, attachments } = indication;
    const specialtyByService = await this.getIndicationsSpecialtyByService(content);

    const isDomicile = content.some((exam) => exam.group === 'LABORATORIO');

    const links = content.map((exam) => {
      const booking = exam?.booking || 'direct';
      const serviceName = (exam.glosa || '').toLocaleUpperCase();
      const codFonasa = exam.codFon || exam.fonasaId ? `${String(exam.codFon || exam.fonasaId)}` : '';
      const fonasaLine = codFonasa
        ? `<div style="margin-top:3px; font-size:11px; color:#0768d0;"><strong>Fonasa:</strong> ${codFonasa}</div>`
        : '';

      const defaultHTML = mailPartialTemplates.examButtonDirect
        .replace('$serviceName$', serviceName)
        .replace('$fonasaLine$', fonasaLine);
      const callHTML = mailPartialTemplates.examButtonCall
        .replace('$serviceName$', serviceName)
        .replace('$fonasaLine$', fonasaLine);

      switch (booking) {
        case 'call':
          return mailPartialTemplates.examButtonCall
            .replace('$serviceName$', serviceName)
            .replace('$fonasaLine$', fonasaLine);

        case 'link':
          const indicationAttach = attachments.find((attach) => attach.category === exam.group.toLowerCase());

          if (!specialtyByService || !specialtyByService.length) {
            if (exam.group === 'IMAGENES') return callHTML;
            return defaultHTML;
          }

          const areaId = this.ambulatoryAgendaService.getAreaId(exam.group, exam.prmId);
          const specialty = specialtyByService.find(
            (srv) =>
              srv &&
              (srv.idServicio === exam.id ||
                String(srv.idServicio).toLocaleLowerCase() === String(exam.prmId).toLocaleLowerCase()),
          );
          if (!specialty || !specialty.idEspecialidad) {
            // Si es examen de imagen, por defecto debe ser llamada
            if (exam.group === 'IMAGENES') return callHTML;
            return defaultHTML;
          }
          const linkQueryParams = new URLSearchParams({
            area: areaId,
            origin: 'RME',
            specialty: String(specialty.idEspecialidad),
            service: String(specialty.idServicio),
            center: 'todos',
            region: EXAM_PATH_NOTIFICATION.regionId,
            patient: patient.documentNumber,
            documentType: 'NationalId',
            medicalOrder: `${EXAM_PATH_NOTIFICATION.path}/${indicationAttach?.key}`,
          }).toString();

          let template = mailPartialTemplates.examButtonLink
            .replace('$serviceName$', serviceName)
            .replace('$fonasaLine$', fonasaLine)
            .replace('$link$', `${BASE_AGENDA_UC}/reserva-horas/seleccion/dia?${linkQueryParams}`);

          return (template =
            exam.group === 'LABORATORIO'
              ? template.replace('$domicilio$', this.getDomicile())
              : template.replace('$domicilio$', ''));

        case 'direct':

        default:
          return defaultHTML;
      }
    });

    if (!links) return '';

    let headerTemplate = mailPartialTemplates.examButtonHeader.replace('$items$', links.join(''));

    headerTemplate = isDomicile
      ? headerTemplate.replace('$examHeaderPartial$', '')
      : headerTemplate.replace('$examHeaderPartial$', '');

    return headerTemplate;
  }

  getHeaderPartialDomicile2() {
    return `<th></th>`;
  }

  getHeaderPartialDomicile() {
    return `<th style='
      box-sizing: border-box;
      text-align: center;
      padding: 16px 0 12px;
      background: #9062F4;
      font-weight: 700;
      font-size: 16px;
      line-height: 20px;
      text-transform: uppercase;
      color: #ffffff;'>Domicilio</th>`;
  }

  getDomicile() {
    return `<a href="https://domicilios.ucchristus.cl/" style="display:inline-block; background:#671e75; padding:8px 10px; border-radius:8px; color:#ffffff; font-size:14px; font-weight:600; text-decoration:none; margin-right:6px; margin-bottom:6px;">Agendar Domicilio</a>`;
  }

  getRadiotherapy() {
    return `
    <p style="text-align: justify;">
      Estimado paciente, a modo de ayuda le recomendamos seguir los siguientes pasos:<br><br>
      1- Para solicitud de Presupuesto de Radioterapia, dirigirse directamente al Piso -1 Asesor de cobertura (horario lunes a jueves de 08:00 a 17:00 horas y viernes 08:00 a 16:00 horas).<br>
      2- En caso de tener un GES y/o CAEC activo en la Red de Salud UC CHRISTUS, debe pasar directamente con Equipo de Enfermeras de Orientación piso -1 (horario lunes a jueves de 08:00 a 17:00 horas y viernes de 08:00 a 16:00 horas).<br>
      3- En caso de alguna inquietud administrativa del proceso, por favor contactarse a <a href="mailto:presupuestosradioterapia@ucchristus.cl">presupuestosradioterapia@ucchristus.cl</a><br>
      4- En caso de alguna inquietud clínica del proceso, por favor contactarse con su Enfermera Coordinadora.
    </p>

  `;
  }

  async getIndicationEOFandDRMLink(paperwork: Paperwork) {
    const { patient, attachments } = paperwork;
    const [content] = paperwork.content;

    const specialtyByService = await this.getIndicationsSpecialtyByService(content.exams || content.procedures);

    const isDomicile =
      content.exams || content.exams?.length
        ? content.exams.some((exam) => exam.group === 'LABORATORIO')
        : content.procedures.some((exam) => exam.group === 'LABORATORIO');

    let links;
    if (content.exams || content.exams?.length) {
      links = content.exams.map((exam) => {
        const booking = exam?.booking || 'direct';
        const serviceName = (exam.glosa || '').toLocaleUpperCase();
        const codFonasa = exam.codFon ? `Código Fonasa ${String(exam.codFon)}` : '';
        const defaultHTML = mailPartialTemplates.examButtonDirect
          .replace('$serviceName$', serviceName)
          .replace('$codFonasa$', codFonasa);
        const callHTML = mailPartialTemplates.examButtonCall
          .replace('$serviceName$', serviceName)
          .replace('$codFonasa$', codFonasa);

        switch (booking) {
          case 'call':
            return mailPartialTemplates.examButtonCall
              .replace('$serviceName$', serviceName)
              .replace('$codFonasa$', codFonasa);

            case 'link':
              const contentGroup = content?.group;

            const indicationAttach = attachments.find((attach) => attach.category === contentGroup.toLowerCase());
            const areaId = exam.areaId;
            const specialty = specialtyByService.find(
              (srv) =>
                srv &&
                (srv.idServicio === exam.id ||
                  String(srv.idServicio).toLocaleLowerCase() === String(exam.prmId).toLocaleLowerCase()),
            );

            if (!specialty || !specialty.idEspecialidad) return callHTML;

            const linkQueryParams = new URLSearchParams({
              origin: 'RME',
              area: areaId,
              center: 'todos',
              specialty: String(specialty.idEspecialidad),
              service: String(specialty.idServicio),
              region: EXAM_PATH_NOTIFICATION.regionId,
              patient: patient.documentNumber,
              documentType: 'NationalId',
              medicalOrder: `${EXAM_PATH_NOTIFICATION.path}/${indicationAttach?.key}`,
            }).toString();
            let template = mailPartialTemplates.examButtonLink
              .replace('$serviceName$', serviceName)
              .replace('$codFonasa$', codFonasa)
              .replace('$link$', `${BASE_AGENDA_UC}/reserva-horas/seleccion/dia?${linkQueryParams}`);

            template =
              exam.group === 'LABORATORIO'
                ? template.replace('$domicilio$', this.getDomicile())
                : template.replace('$domicilio$', '');
            return template;

          case 'direct':
            break;

          default:
            return defaultHTML;
        }
      });
    }

    if (content.procedures || content.procedures?.length) {
      links = content.procedures.map((exam) => {
        const booking = exam?.booking || 'call';
        const serviceName = (exam.glosa || '').toLocaleUpperCase();
        const codFonasa = exam.code ? `Código Fonasa ${String(exam.code)}` : '';
        const defaultHTML = mailPartialTemplates.examButtonDirect
          .replace('$serviceName$', serviceName)
          .replace('$codFonasa$', codFonasa);
        const callHTML = mailPartialTemplates.examButtonCall
          .replace('$serviceName$', serviceName)
          .replace('$codFonasa$', codFonasa);

        switch (booking) {
          case 'call':
            return mailPartialTemplates.examButtonCall
              .replace('$serviceName$', serviceName)
              .replace('$codFonasa$', codFonasa);

          case 'link':

          case 'direct':

          default:
            return defaultHTML;
        }
      });
    }

    if (!links) return '';
    let headerTemplate = mailPartialTemplates.examButtonHeader.replace('$items$', links.join(''));

    headerTemplate = isDomicile
      ? headerTemplate.replace('$examHeaderPartial$', this.getHeaderPartialDomicile())
      : headerTemplate.replace('$examHeaderPartial$', '');

    return headerTemplate;
  }

  async getIndicationOTRLink(paperwork: Paperwork) {
    const { patient, attachments } = paperwork;
    const [content] = paperwork.content;

    const contentGroup = content?.group;

    const specialtyByService = await this.getIndicationsSpecialtyByService(content.listExam);

    const isDomicile = content.listExam.some((exam) => exam.group === 'LABORATORIO');

    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    let links: any[];

    if (content.listExam || content.listExam?.length) {
      links = content.listExam.map((exam) => {
        const booking = exam?.booking || 'direct';
        const serviceName = (exam.glosa || '').toLocaleUpperCase();
        const codFonasa = exam.codFon ? `Código Fonasa ${String(exam.codFon)}` : '';
        const defaultHTML = mailPartialTemplates.examButtonDirect
          .replace('$serviceName$', serviceName)
          .replace('$codFonasa$', codFonasa);
        const callHTML = mailPartialTemplates.examButtonCall
          .replace('$serviceName$', serviceName)
          .replace('$codFonasa$', codFonasa);

        switch (booking) {
          case 'call':
            return mailPartialTemplates.examButtonCall
              .replace('$serviceName$', serviceName)
              .replace('$codFonasa$', codFonasa);

          case 'link':
            if (!specialtyByService || !specialtyByService.length) return callHTML;

            const indicationAttach = attachments.find((attach) => attach.category === contentGroup.toLowerCase());
            const areaId = this.ambulatoryAgendaService.getAreaId(exam.group, exam.prmId);
            const specialty = specialtyByService.find(
              (srv) =>
                srv &&
                (srv.idServicio === exam.id ||
                  String(srv.idServicio).toLocaleLowerCase() === String(exam.prmId).toLocaleLowerCase()),
            );

            if (!specialty || !specialty.idEspecialidad) return callHTML;

            const linkQueryParams = new URLSearchParams({
              origin: 'RME',
              area: areaId,
              center: 'todos',
              specialty: String(specialty.idEspecialidad),
              service: String(specialty.idServicio),
              region: EXAM_PATH_NOTIFICATION.regionId,
              patient: patient.documentNumber,
              documentType: 'NationalId',
              medicalOrder: `${EXAM_PATH_NOTIFICATION.path}/${indicationAttach?.key}`,
            }).toString();

            let template = mailPartialTemplates.examButtonLink
              .replace('$serviceName$', serviceName)
              .replace('$codFonasa$', codFonasa)
              .replace('$link$', `${BASE_AGENDA_UC}/reserva-horas/seleccion/dia?${linkQueryParams}`);

            template =
              exam.group === 'LABORATORIO'
                ? template.replace('$domicilio$', this.getDomicile())
                : template.replace('$domicilio$', '');
            return template;

          case 'direct':
            break;

          default:
            return defaultHTML;
        }
      });
    }

    if (!links) return '';
    let headerTemplate = mailPartialTemplates.examButtonHeader.replace('$items$', links.join(''));

    return isDomicile
      ? headerTemplate.replace('$examHeaderPartial$', this.getHeaderPartialDomicile())
      : headerTemplate.replace('$examHeaderPartial$', '');
  }

  /**
   * Obtener las rutas a los documentos que se deben enviar adjuntos en el correo electrónico.
   *
   * @param attachments Archivos adjuntos desde el formulario o pdfs generados al trámite.
   */
  getAttachmentsForEmail(attachments: Attachment[]): ShortPathFile[] {
    if (!attachments || !Array.isArray(attachments) || attachments.length === 0) return [];

    return attachments
      .filter((attach) => attach && attach.isAttached)
      .map((attach) => {
        const key = attach.key;
        const path = `${attach.bucket}/${attach.path}`;

        return { path, key };
      });
  }

  /**
   * Obtener el cuerpo de la petición de correo electrónico.
   *
   * @param paperworkState - Trámite extraído del estado global de la aplicación.
   */
  async getEmailRequest(paperworkState: PaperworkState, isSurgery = false) {    
    const { attachments } = paperworkState.paperwork;

    /**
     * Determina los archivos adjuntos relevantes para una cirugía.
     *
     * Si el estado del trámite (`paperworkState`) corresponde a una cirugía (según el prefijo `PREFIX_SURGERY`)
     * y el sufijo del submódulo es 'ODC', selecciona un archivo específico de la lista de adjuntos (`attachments`)
     * dependiendo del valor de `isSurgery` (Para paciente el pdf normal y para profesionales el pdf Keiron). En caso contrario, retorna todos los adjuntos.
     *
     */
    const attachmentsSurgery =
      paperworkState.paperwork.prefix === PREFIX_SURGERY && paperworkState.definition.submodule.suffix === 'ODC'
      ? [attachments[isSurgery ? 1 : 0]]
      : attachments;

    const notifications = paperworkState.definition.submodule.workflow.notifications;
    const additionalContent = await this.getEmailAdditionalContent(paperworkState);
    const posts: any[] = [];
    for (const notify of notifications) {
      const to = this.getNotifyTo(notify, paperworkState, isSurgery);
      const cc = this.getNotifyCC(notify, paperworkState);
      const cco = notify?.cco || [];
      const subject = this.getSubject(notify, paperworkState);
      const files = this.getAttachments(notify, attachmentsSurgery ? attachmentsSurgery : attachments);
      const content = await this.getEmailBodyHTML(notify.template, paperworkState, additionalContent);
      if (to && to.length > 0) posts.push({ to, cc, cco, subject, content, attachments: files });
    }
    return posts;
  }

  /**
   * Obtiene información adicional al trámite que es necesaria para la construcción del correo
   */
  async getEmailAdditionalContent(paperworkState: PaperworkState) {
    const { paperwork, definition } = paperworkState;
    if (paperwork.prefix === PREFIX_RECIPE) return await this.getRecipeQuotePharmaEmailBodyHTML(paperwork);

    if (paperwork.prefix === PREFIX_INDICATION && definition.submodule.suffix === 'EXA')
      return await this.getIndicationLink(paperwork);

    if (paperwork.prefix === PREFIX_INDICATION && ['EOF'].includes(definition.submodule.suffix)) {
      return await this.getIndicationEOFandDRMLink(paperwork);
    }

    if (paperwork.prefix === PREFIX_INDICATION && ['DRM'].includes(definition.submodule.suffix)) {
      return await this.getIndicationEOFandDRMLink(paperwork);
    }

    if (paperwork.prefix === PREFIX_INDICATION && ['OTR'].includes(definition.submodule.suffix)) {
      return await this.getIndicationOTRLink(paperwork);
    }

    return null;
  }

  getAttachments(notify: WorkflowNotify, attachments: Attachment[]) {
    if (notify.entity === 'indications.exams.images.team')
      return this.getAttachmentsForEmail(attachments.filter((attach) => attach.category === 'imagenes'));

    return this.getAttachmentsForEmail(attachments);
  }

  getSubject(notify: WorkflowNotify, paperworkState: PaperworkState): string {
    const isTemplateSubject = notify.subject.includes('$');
    if (!isTemplateSubject) return notify.subject;

    const { patient, shortId } = paperworkState.paperwork;
    return notify.subject.replace('$patient$', patient.documentNumber).replace('$shortId$', shortId);
  }

  getNotifyTo(notify: WorkflowNotify, paperworkState: PaperworkState, isSurgery = false): string[] {
    const { paperwork, definition } = paperworkState;

    // si el profesional es TENS no envía notificación a paciente
    const entityBaseEmail =
      notify.entity === 'patient' && this.isNoMailSubprofessionalTENS() ? undefined : paperwork[notify.entity]?.email;

    const entityEmails = this.notificationEmailsService.getEntityEmails(notify.entity, paperworkState);
    if (entityEmails?.to && Array.isArray(entityEmails.to)) return [entityBaseEmail, ...entityEmails.to];

    if (
      paperwork.prefix === PREFIX_ONCOLOGY &&
      (definition.submodule.suffix === 'RTP' || definition.submodule.suffix === 'RPO')
    )
      return this.mailsByForm(paperwork);

    if (paperwork.prefix === 'DER' && definition.submodule.suffix === 'ITC') return this.mailsByDerivations(paperwork);

    if (paperwork.prefix === PREFIX_SURGERY && definition.submodule.suffix === 'ODC' && isSurgery)
      return this.mailsSurgeryByProfesional(paperwork);

    if (notify.entity === 'indications.exams.images.team') return this.mailsIndicationImagesTeam(paperwork);

    return entityBaseEmail ? [entityBaseEmail] : [];
  }

  getNotifyCC(notify: WorkflowNotify, paperworkState: PaperworkState): string[] {
    const { paperwork, definition } = paperworkState;
    let cc = notify?.cc || [];

    const entityEmails = this.notificationEmailsService.getEntityEmails(notify.entity, paperworkState);
    if (entityEmails?.cc && Array.isArray(entityEmails.cc)) cc = cc.concat(entityEmails.cc);

    if (entityEmails?.cc && definition.submodule.suffix === 'ITC')
      cc = cc.concat(this.mailsDerivationBySpecialty(paperwork, entityEmails?.cc));

    if (entityEmails?.cc && definition.submodule.suffix === 'GES')
      cc = cc.concat(this.mailsGesByCenter(paperwork, entityEmails?.cc));

    if (entityEmails?.cc && definition.submodule.suffix === 'RTP')
      cc = cc.concat(this.mailsOncByCenter(paperwork, entityEmails?.cc));

    if (definition.submodule.suffix === 'ODH') cc = cc.concat(this.mailsHospByCenter(paperworkState));

    const accessTeamCc = this.getNotifyAccessTeamEmails(paperworkState);
    return [...cc, ...(accessTeamCc || [])];
  }

  mailsIndicationImagesTeam(paperwork: Paperwork): string[] {
    const hasImageIndications = paperwork.content.some((exam) => exam.group === 'IMAGENES');
    if (hasImageIndications) return environment.teamImagesEmails;
    return [];
  }

  mailsDerivationBySpecialty(paperwork: Paperwork, mailsList: any): string[] {
    const [derivation] = paperwork.content;
    const specialty = derivation.service?.derivation?.specialty;
    if (!specialty) return [];
    return mailsList[specialty.toString().trim().toLowerCase()] || [];
  }

  mailsGesByCenter(paperwork: Paperwork, mailsList: any): string[] {
    const [form] = paperwork.content;
    const center = form.center.name;
    if (!center) return [];
    return mailsList[center.toString().trim().toLowerCase()] || [];
  }

  mailsOncByCenter(paperwork: Paperwork, mailsList: any): string[] {
    const [form] = paperwork.content;
    const center = form.site;

    if (!center) return [];
    return mailsList[center.toString().trim().toLowerCase()] || [];
  }

  mailsHospByCenter(paperworkState: PaperworkState): string[] {
    const { paperwork } = paperworkState;
    const { content } = paperwork;
    const { placeToBeHospitalized: center } = content[0];
    if (!center) return [];
    return (
      EMAILS[STAGE]?.[paperwork.prefix.toLowerCase()]?.[paperwork.suffix.toLowerCase()]?.['byCenter']?.cc[center] || []
    );
  }

  mailsSurgeryByProfesional(paperwork: Paperwork): string[] {
    const { professional } = paperwork;
    return EMAIL_DERIVATE_BY_PROFESIONAL[professional.rut] || [];
  }

  mailsByForm(form: Paperwork): string[] {
    const ROADMAP_MAIL_MR = [];
    if (form.content[0]?.nurses)
      form.content[0]?.nurses.forEach((nurse) => {
        ROADMAP_MAIL_MR.push(nurse.email);
      });
    const emailList: string[] = [form.patient.email];
    if (!environment.isProduction) return emailList;

    switch (form.content[0].group) {
      case FormsOncology.radioterapia:
        if (form.content[0].site === CENTER_MC) emailList.push(...MAIL_RADIOTHERAPY_MARCOLETA);
        if (form.content[0].site === CENTER_CSC) emailList.push(...MAIL_RADIOTHERAPY_CSC);
        break;
      case FormsOncology.roadmap:
        if (form.content[0].path === 'quimioterapia-hoja-ruta-ceca/csc') emailList.push(...NURSES_SC_MAILS);
        if (form.content[0].path === 'quimioterapia-hoja-ruta-ceca/cem')
          emailList.push(...NURSES_MC_MAILS, ...ROADMAP_MAIL_MR);
        break;
    }
    return emailList;
  }

  mailsByDerivations(form: Paperwork): string[] {
    const emailList: string[] = [form.patient.email];
    switch (form.content[0].interconsultation[0]?.name || form.content[0].service?.derivation?.specialty) {
      case SPECIALTIES.balance:
      case SPECIALTIES.balance2:
      case SPECIALTIES.balance3:
        emailList.push(...MAIL_DERIVATIONS.balance);
        break;
      case SPECIALTIES.unidad:
        emailList.push(...MAIL_DERIVATIONS.unidad);
        break;
      case SPECIALTIES.programa:
        emailList.push(...MAIL_DERIVATIONS.programa);
        break;
      case SPECIALTIES.programa_uc:
        emailList.push(...MAIL_DERIVATIONS.programa_uc);
        break;
      case SPECIALTIES.seguimiento:
        emailList.push(...MAIL_DERIVATIONS.seguimiento);
        break;
      case SPECIALTIES.cefaleas:
        emailList.push(...MAIL_DERIVATIONS.cefaleas);
        break;
      case SPECIALTIES.pemp:
        emailList.push(...MAIL_DERIVATIONS.pemp);
        break;
    }
    return emailList;
  }

  /**
   * Obtener las promesas de las solicitudes al servicio para enviar correos electrónicos.
   *
   * @param paperwork
   */
  async getRequestsToSendMail(paperworkState: PaperworkState): Promise<Observable<Metadata>[]> {
    const { paperwork } = paperworkState;
    const body = await this.getEmailRequest(paperworkState);
    if (paperwork.prefix === PREFIX_SURGERY && paperwork.suffix === 'ODC')
      body.push(...await this.getEmailRequest(paperworkState, true));

    const requests = body.map((params) => this.sendEmail(params));
    return requests;
  }

  /**
   * Obtener contenido de correo de Acceso Dedicado
   *
   * @param paperworkState
   */
  private getNotifyAccessTeamContent(paperworkState: PaperworkState): string {
    const { nextControl } = paperworkState.paperwork;
    const isCcTeamAccess = nextControl?.ccTeamAccess && nextControl.ccTeamAccess !== '';

    if (!isCcTeamAccess) return '';
    return getMailTeamAccessText(nextControl);
  }

  /**
   * Obtener lista de correos para Acceso Dedicado
   *
   * @param paperworkState
   */
  private getNotifyAccessTeamEmails(paperworkState: PaperworkState): string[] {
    const { nextControl } = paperworkState.paperwork;
    const isCcTeamAccess = nextControl?.ccTeamAccess && nextControl.ccTeamAccess !== '';
    if (!isCcTeamAccess) return [];
    return nextControl?.ccTeamAccessMails || [];
  }
}
